import { useState } from "react";
import { motion } from "framer-motion";
import { useIsMobile } from "../../hooks/useIsMobile";

/* ------------------------------------------------------------------
   Mad House — Award Logo Strip

   Slim cinematic recognition strip. All text rendered in a uniform
   metallic gold gradient. Each lockup carries a real festival logo
   on its left, fetched from the web with a graceful fallback chain:
       1. Clearbit logo API     (sharpest, transparent PNG)
       2. Google s2 favicon     (small but very reliable)
       3. Diamond glyph         (final cinematic fallback)

   Two render modes:
     • Default          → standalone <section> with its own borders.
     • embedded={true}  → <div>, transparent, no top border. Used inside
                          the landing reel so the strip looks like a
                          footer band of the slide placeholder.
   ------------------------------------------------------------------ */

const LOGOS = [
    {
        id: "kyoorius",
        line1: "Kyoorius",
        line2: "Blue Elephant",
        domain: "kyoorius.com"
    },
    {
        id: "yda",
        line1: "YDA",
        line2: "Cannes",
        domain: "ydawards.com"
    },
    {
        id: "ciclope",
        line1: "Ciclope",
        line2: "Festival",
        domain: "ciclopefestival.com"
    },
    {
        id: "shots",
        line1: "Shots",
        line2: "Asia Pacific",
        domain: "shots.net"
    },
    {
        id: "gam",
        line1: "Good Ads",
        line2: "Matter",
        domain: "goodadsmatter.org"
    },
    {
        id: "berlin",
        line1: "Berlin",
        line2: "Commercial",
        domain: "berlincommercial.com"
    }
];

// Shared metallic gold gradient — used by all text via background-clip.
const GOLD_GRADIENT =
    "linear-gradient(180deg, #f4d691 0%, #e7b95d 45%, #c98a3b 78%, #9c6720 100%)";

// Logo CSS filter — desaturates the source artwork into a unified
// silvery-gold tone so all 6 logos coexist visually without clashing.
const LOGO_FILTER =
    "grayscale(0.55) sepia(0.55) saturate(1.4) brightness(1.05)";

export const AwardLogoStrip = ({ embedded = false }) => {
    const isMobile = useIsMobile();
    // Three copies for a seamless mh-marquee loop (.mh-marquee shifts -33.33%).
    const row = [...LOGOS, ...LOGOS, ...LOGOS];

    const Wrapper = embedded ? "div" : "section";
    const wrapperClass = embedded
        ? "relative isolate overflow-hidden"
        : "relative isolate overflow-hidden border-y border-ink-border bg-ink-surface/40 backdrop-blur-md";

    return (
        <Wrapper
            data-testid="award-logo-strip"
            className={wrapperClass}
            aria-label="Award recognition"
        >
            {/* Hairline top divider */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                    background: embedded
                        ? "linear-gradient(90deg, transparent 0%, rgba(238,200,110,0.30) 50%, transparent 100%)"
                        : "linear-gradient(90deg, transparent 0%, rgba(238,200,110,0.22) 50%, transparent 100%)"
                }}
            />
            {!embedded ? (
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
                    style={{
                        background:
                            "linear-gradient(90deg, transparent 0%, rgba(238,200,110,0.18) 50%, transparent 100%)"
                    }}
                />
            ) : null}

            {/* Soft side vignettes */}
            <div
                aria-hidden
                className={`pointer-events-none absolute inset-y-0 left-0 z-10 ${
                    embedded ? "w-12 md:w-20" : "w-16 md:w-32"
                } bg-gradient-to-r from-ink to-transparent`}
            />
            <div
                aria-hidden
                className={`pointer-events-none absolute inset-y-0 right-0 z-10 ${
                    embedded ? "w-12 md:w-20" : "w-16 md:w-32"
                } bg-gradient-to-l from-ink to-transparent`}
            />

            {/* Slow shimmer pass — desktop only */}
            {!isMobile ? (
                <motion.div
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 -left-1/4 z-[5] w-1/3"
                    style={{
                        background:
                            "linear-gradient(110deg, transparent 0%, rgba(238,200,110,0.10) 50%, transparent 100%)"
                    }}
                    initial={{ x: "-40%" }}
                    animate={{ x: ["-40%", "420%"] }}
                    transition={{
                        duration: 8.5,
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatDelay: 5
                    }}
                />
            ) : null}

            {/* Eyebrow label — only in standalone mode */}
            {!embedded ? (
                <div
                    aria-hidden
                    className="pointer-events-none absolute left-6 top-1/2 z-20 hidden -translate-y-1/2 items-center gap-2 font-mono text-[9px] uppercase tracking-[0.45em] md:left-12 md:flex md:text-[10px]"
                    style={{
                        background: GOLD_GRADIENT,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        opacity: 0.7
                    }}
                >
                    <span
                        className="inline-block h-[5px] w-[5px] rotate-45"
                        style={{ background: "#eec86e" }}
                    />
                    <span>/ Recognized at</span>
                </div>
            ) : null}

            {/* Marquee track */}
            <div className={embedded ? "relative py-3 md:py-4" : "relative py-5 md:py-7"}>
                <div className="mh-marquee whitespace-nowrap">
                    {row.map((logo, i) => (
                        <LogoCell
                            key={`logo-${i}`}
                            logo={logo}
                            embedded={embedded}
                        />
                    ))}
                </div>
            </div>
        </Wrapper>
    );
};

/* ------------------------------------------------------------------
   Single logo cell — image + two-line gold lockup.
   ------------------------------------------------------------------ */

const LogoCell = ({ logo, embedded }) => (
    <span
        data-testid={`award-logo-${logo.id}`}
        className={`group/logo relative inline-flex items-center transition-opacity duration-500 hover:opacity-100 ${
            embedded ? "mx-6 md:mx-10" : "mx-7 md:mx-12"
        }`}
        style={{ opacity: 0.85 }}
    >
        <span className="flex flex-col leading-[1.05]">
            <span
                className="font-display uppercase tracking-tight"
                style={{
                    fontSize: embedded
                        ? "clamp(0.9rem, 1.2vw, 1.15rem)"
                        : "clamp(1rem, 1.4vw, 1.35rem)",
                    background: GOLD_GRADIENT,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent"
                }}
            >
                {logo.line1}
            </span>
            <span
                className="font-mono text-[9px] uppercase tracking-[0.32em] md:text-[10px]"
                style={{
                    background: GOLD_GRADIENT,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent",
                    opacity: 0.78
                }}
            >
                {logo.line2}
            </span>
        </span>
    </span>
);

/* ------------------------------------------------------------------
   Logo image with a 3-tier fallback chain. Always yields *something*.
   ------------------------------------------------------------------ */

const LogoImg = ({ domain, alt, embedded }) => {
    const sources = [
        `https://logo.clearbit.com/${domain}`,
        `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
    ];
    const [srcIdx, setSrcIdx] = useState(0);
    const [failed, setFailed] = useState(false);

    const sizeCls = embedded
        ? "h-7 w-7 md:h-8 md:w-8"
        : "h-8 w-8 md:h-10 md:w-10";

    if (failed) {
        return (
            <span
                aria-hidden
                aria-label={alt}
                className={`inline-flex shrink-0 items-center justify-center rounded-full border border-[#eec86e]/40 bg-[#eec86e]/[0.08] ${sizeCls}`}
            >
                <span
                    className="inline-block h-[6px] w-[6px] rotate-45"
                    style={{
                        background: "#eec86e",
                        boxShadow: "0 0 10px rgba(238,200,110,0.55)"
                    }}
                />
            </span>
        );
    }

    return (
        <span
            aria-hidden
            className={`relative inline-flex shrink-0 items-center justify-center rounded-full border border-[#eec86e]/30 bg-[#eec86e]/[0.06] p-1.5 backdrop-blur-sm ${sizeCls}`}
        >
            <img
                src={sources[srcIdx]}
                alt={alt}
                draggable={false}
                referrerPolicy="no-referrer"
                onError={() => {
                    if (srcIdx < sources.length - 1) {
                        setSrcIdx(srcIdx + 1);
                    } else {
                        setFailed(true);
                    }
                }}
                className="block h-full w-full object-contain"
                style={{ filter: LOGO_FILTER }}
            />
        </span>
    );
};

export default AwardLogoStrip;
